import { useEffect, useRef, useCallback, useMemo } from 'react';
import { Tooltip, Dropdown } from 'bootstrap';

/**
 * Enhanced Bootstrap components hook with better error handling
 * This version initializes both tooltips and dropdowns and uses a more defensive approach to prevent transition-related errors
 */
export function useBootstrapComponents(
  dependencies = [],
  tooltipSelector = '[data-bs-toggle="tooltip"]',
  dropdownSelector = '[data-bs-toggle="dropdown"]',
  options = {}
) {
  const tooltipMapRef = useRef(new Map());
  const dropdownMapRef = useRef(new Map());
  const initTimeoutRef = useRef(null);

  // Safely dispose a single tooltip
  const safeDisposeTooltip = useCallback((tooltip, element) => {
    return new Promise(resolve => {
      try {
        // Check if tooltip and element are valid
        if (!tooltip || typeof tooltip.dispose !== 'function') {
          resolve();
          return;
        }

        // Check if element exists and is still connected to DOM
        if (!element || !element.isConnected || !document.contains(element)) {
          // Element is no longer in DOM, force dispose
          try {
            tooltip.dispose();
          } catch (error) {
            console.warn('Error force disposing tooltip for removed element:', error);
          }
          resolve();
          return;
        }

        // Check if tooltip is currently shown
        const isShown =
          element.getAttribute && element.getAttribute('aria-describedby')?.includes('tooltip');

        if (isShown) {
          // If tooltip is shown, hide it first and wait for hide event
          const handleHidden = () => {
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.tooltip', handleHidden);
            }
            try {
              tooltip.dispose();
            } catch (error) {
              console.warn('Error disposing tooltip after hide:', error);
            }
            resolve();
          };

          if (element.addEventListener) {
            element.addEventListener('hidden.bs.tooltip', handleHidden, { once: true });
          }

          try {
            tooltip.hide();
          } catch (error) {
            // If hide fails, cleanup and dispose directly
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.tooltip', handleHidden);
            }
            try {
              tooltip.dispose();
            } catch (disposeError) {
              console.warn('Error disposing tooltip after hide failure:', disposeError);
            }
            resolve();
          }

          // Fallback timeout in case hidden event doesn't fire
          setTimeout(() => {
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.tooltip', handleHidden);
            }
            try {
              tooltip.dispose();
            } catch (error) {
              console.warn('Error disposing tooltip (timeout fallback):', error);
            }
            resolve();
          }, 300);
        } else {
          // Tooltip not shown, dispose directly
          try {
            tooltip.dispose();
          } catch (error) {
            console.warn('Error disposing hidden tooltip:', error);
          }
          resolve();
        }
      } catch (error) {
        console.warn('Error in safeDisposeTooltip:', error);
        resolve();
      }
    });
  }, []);

  // Safely dispose a single dropdown
  const safeDisposeDropdown = useCallback((dropdown, element) => {
    return new Promise(resolve => {
      try {
        // Check if dropdown and element are valid
        if (!dropdown || typeof dropdown.dispose !== 'function') {
          resolve();
          return;
        }

        // Check if element exists and is still connected to DOM
        if (!element || !element.isConnected || !document.contains(element)) {
          // Element is no longer in DOM, force dispose
          try {
            dropdown.dispose();
          } catch (error) {
            console.warn('Error force disposing dropdown for removed element:', error);
          }
          resolve();
          return;
        }

        // Check if dropdown is currently shown
        const isShown = element.getAttribute && element.getAttribute('aria-expanded') === 'true';

        if (isShown) {
          // If dropdown is shown, hide it first and wait for hide event
          const handleHidden = () => {
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.dropdown', handleHidden);
            }
            try {
              dropdown.dispose();
            } catch (error) {
              console.warn('Error disposing dropdown after hide:', error);
            }
            resolve();
          };

          if (element.addEventListener) {
            element.addEventListener('hidden.bs.dropdown', handleHidden, { once: true });
          }

          try {
            dropdown.hide();
          } catch (error) {
            // If hide fails, cleanup and dispose directly
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.dropdown', handleHidden);
            }
            try {
              dropdown.dispose();
            } catch (disposeError) {
              console.warn('Error disposing dropdown after hide failure:', disposeError);
            }
            resolve();
          }

          // Fallback timeout in case hidden event doesn't fire
          setTimeout(() => {
            if (element && element.removeEventListener) {
              element.removeEventListener('hidden.bs.dropdown', handleHidden);
            }
            try {
              dropdown.dispose();
            } catch (error) {
              console.warn('Error disposing dropdown (timeout fallback):', error);
            }
            resolve();
          }, 300);
        } else {
          // Dropdown not shown, dispose directly
          try {
            dropdown.dispose();
          } catch (error) {
            console.warn('Error disposing hidden dropdown:', error);
          }
          resolve();
        }
      } catch (error) {
        console.warn('Error in safeDisposeDropdown:', error);
        resolve();
      }
    });
  }, []);

  // Initialize tooltips safely
  const initializeTooltips = useCallback(() => {
    try {
      const defaultOptions = {
        placement: 'top',
        trigger: 'hover focus',
        delay: { show: 500, hide: 100 },
        container: 'body',
        // Disable animations to prevent transition issues
        animation: false,
        // Custom template to avoid potential issues
        template:
          '<div class="tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
        ...options,
      };

      const tooltipTriggerList = document.querySelectorAll(tooltipSelector);

      tooltipTriggerList.forEach(element => {
        try {
          // Skip if element is not connected to DOM
          if (!element || !element.isConnected || !document.contains(element)) {
            return;
          }

          // Skip if we already have a tooltip for this element
          if (tooltipMapRef.current.has(element)) {
            return;
          }

          // Check for existing Bootstrap tooltip instance
          const existingTooltip = Tooltip.getInstance(element);
          if (existingTooltip) {
            safeDisposeTooltip(existingTooltip, element);
          }

          // Create new tooltip with enhanced error handling
          const tooltip = new Tooltip(element, defaultOptions);
          tooltipMapRef.current.set(element, tooltip);

          // Add error event listeners
          element.addEventListener('show.bs.tooltip', event => {
            // Additional safety check before showing
            if (!element.isConnected || !document.contains(element)) {
              event.preventDefault();
              return false;
            }
          });
        } catch (error) {
          console.warn('Failed to initialize tooltip for element:', element, error);
        }
      });
    } catch (error) {
      console.warn('Error in initializeTooltips:', error);
    }
  }, [tooltipSelector, options, safeDisposeTooltip]);

  // Initialize dropdowns safely
  const initializeDropdowns = useCallback(() => {
    try {
      const dropdownTriggerList = document.querySelectorAll(dropdownSelector);

      dropdownTriggerList.forEach(element => {
        try {
          // Skip if element is not connected to DOM
          if (!element || !element.isConnected || !document.contains(element)) {
            return;
          }

          // Skip if we already have a dropdown for this element
          if (dropdownMapRef.current.has(element)) {
            return;
          }

          // Check for existing Bootstrap dropdown instance
          const existingDropdown = Dropdown.getInstance(element);
          if (existingDropdown) {
            safeDisposeDropdown(existingDropdown, element);
          }

          // Create new dropdown with enhanced error handling
          const dropdown = new Dropdown(element);
          dropdownMapRef.current.set(element, dropdown);

          // Add error event listeners
          element.addEventListener('show.bs.dropdown', event => {
            // Additional safety check before showing
            if (!element.isConnected || !document.contains(element)) {
              event.preventDefault();
              return false;
            }
          });
        } catch (error) {
          console.warn('Failed to initialize dropdown for element:', element, error);
        }
      });
    } catch (error) {
      console.warn('Error in initializeDropdowns:', error);
    }
  }, [dropdownSelector, safeDisposeDropdown]);

  // Cleanup all tooltips
  const cleanupAllTooltips = useCallback(async () => {
    const disposePromises = [];
    const elementsToRemove = [];

    for (const [element, tooltip] of tooltipMapRef.current.entries()) {
      // Validate element before disposing
      if (!element || !document.contains(element)) {
        // Element is no longer in DOM, dispose immediately and mark for removal
        try {
          if (tooltip && typeof tooltip.dispose === 'function') {
            tooltip.dispose();
          }
        } catch (error) {
          console.warn('Error disposing tooltip for removed element:', error);
        }
        elementsToRemove.push(element);
      } else {
        disposePromises.push(safeDisposeTooltip(tooltip, element));
      }
    }

    // Remove invalid elements from map
    elementsToRemove.forEach(element => {
      tooltipMapRef.current.delete(element);
    });

    // Wait for all valid disposals to complete
    if (disposePromises.length > 0) {
      await Promise.all(disposePromises);
    }

    tooltipMapRef.current.clear();
  }, [safeDisposeTooltip]);

  // Cleanup all dropdowns
  const cleanupAllDropdowns = useCallback(async () => {
    const disposePromises = [];
    const elementsToRemove = [];

    for (const [element, dropdown] of dropdownMapRef.current.entries()) {
      // Validate element before disposing
      if (!element || !document.contains(element)) {
        // Element is no longer in DOM, dispose immediately and mark for removal
        try {
          if (dropdown && typeof dropdown.dispose === 'function') {
            dropdown.dispose();
          }
        } catch (error) {
          console.warn('Error disposing dropdown for removed element:', error);
        }
        elementsToRemove.push(element);
      } else {
        disposePromises.push(safeDisposeDropdown(dropdown, element));
      }
    }

    // Remove invalid elements from map
    elementsToRemove.forEach(element => {
      dropdownMapRef.current.delete(element);
    });

    // Wait for all valid disposals to complete
    if (disposePromises.length > 0) {
      await Promise.all(disposePromises);
    }

    dropdownMapRef.current.clear();
  }, [safeDisposeDropdown]);

  // Cleanup all components
  const cleanupAllComponents = useCallback(async () => {
    await Promise.all([cleanupAllTooltips(), cleanupAllDropdowns()]);
  }, [cleanupAllTooltips, cleanupAllDropdowns]);

  // Initialize all components
  const initializeAllComponents = useCallback(() => {
    initializeTooltips();
    initializeDropdowns();
  }, [initializeTooltips, initializeDropdowns]);

  // Serialize dependencies to create a stable reference for ESLint
  const dependenciesString = useMemo(() => {
    try {
      return JSON.stringify(dependencies);
    } catch {
      // Fallback for non-serializable dependencies
      return dependencies.length.toString();
    }
  }, [dependencies]);

  useEffect(() => {
    // Clear any pending initialization
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    // First cleanup existing components
    cleanupAllComponents().then(() => {
      // Then initialize new ones with a small delay
      initTimeoutRef.current = setTimeout(initializeAllComponents, 50);
    });

    // Cleanup function
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      cleanupAllComponents();
    };
  }, [cleanupAllComponents, initializeAllComponents, dependenciesString]);

  // Return manual control functions if needed
  return {
    reinitialize: () => {
      cleanupAllComponents().then(() => {
        setTimeout(initializeAllComponents, 50);
      });
    },
    cleanup: cleanupAllComponents,
  };
}

// Keep the old export for backwards compatibility with existing tooltip usage
export const useBootstrapTooltips = useBootstrapComponents;

export default useBootstrapComponents;
